import api from './api';

export interface AggregatorSummary {
  channelId?: number;
  channelName?: string;
  dateFrom: string;
  dateTo: string;
  reportedGross: number;
  settled: number;
  pending: number;
  commissionActual: number;
  effectiveCommissionPercent: number;
  unexplainedVariance: number;
  settlementCount: number;
  entryCount: number;
}

export interface AggregatorEntry {
  id: number;
  entryType: string;
  channelId?: number;
  channelName?: string;
  date: string;
  grossAmount: number;
  netAmount?: number;
  commissionAmount?: number;
  settlementId?: number | null;
  settlementStatus: 'PENDING' | 'PARTIAL' | 'SETTLED';
  allocatedAmount?: number | null;
  ageDays: number;
}

export interface SettlementAllocation {
  id?: number;
  entryType: string;
  entryId: number;
  entryDate?: string;
  entryGrossAmount?: number;
  allocatedAmount: number;
  sourceLabel?: string;
}

export interface AggregatorSettlement {
  id: number;
  channelId: number;
  channelName: string;
  paymentDate: string;
  periodFrom?: string | null;
  periodTo?: string | null;
  paymentAmount: number;
  grossReported: number;
  commissionExpected: number;
  commissionActual: number;
  commissionDrift: number;
  adjustmentAmount: number;
  unexplainedVariance: number;
  status: string;
  bankExternalRef?: string | null;
  source: string;
  notes?: string | null;
  commissionExpenseId?: number | null;
  adjustmentExpenseId?: number | null;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  allocations: SettlementAllocation[];
}

export interface AgingBucket {
  label: string;
  count: number;
  total: number;
}

export interface AgingResponse {
  buckets: AgingBucket[];
  topPending: AggregatorEntry[];
}

export interface SuggestedAllocations {
  suggested: SettlementAllocation[];
  cumulativeGross: number;
  residual: number;
  windowStart: string;
  windowEnd: string;
}

export type AdjustmentKind = 'REFUND' | 'CHARGEBACK' | 'PROMO' | 'TIP_CORRECTION' | 'OTHER';

export interface SettlementAdjustment {
  id: number;
  settlementId: number;
  adjustmentDate: string;
  amount: number;
  kind: AdjustmentKind;
  note?: string | null;
  createdAt?: string;
  createdBy?: string;
}

export interface DuplicateAllocation {
  entryType: string;
  entryId: number;
  entryDate?: string;
  entryGross?: number;
  channelName?: string;
  settlementIds: number[];
  count: number;
}

export interface ChannelAlias {
  id: number;
  channelId: number;
  channelName: string;
  pattern: string;
  patternNormalized: string;
}

export interface RecordSettlementInput {
  channelId: number;
  paymentDate: string;
  periodFrom?: string;
  periodTo?: string;
  paymentAmount: number;
  bankExternalRef?: string;
  source?: 'MANUAL' | 'BANK_IMPORT';
  notes?: string;
  adjustmentAmount?: number;
  allocations?: Array<{ entryType: string; entryId: number; allocatedAmount: number }>;
}

export interface EditSettlementInput {
  paymentDate?: string;
  periodFrom?: string;
  periodTo?: string;
  paymentAmount?: number;
  adjustmentAmount?: number;
  notes?: string;
  allocations?: Array<{ entryType: string; entryId: number; allocatedAmount: number }>;
}

const BASE = '/api/vendor/finance/aggregators';

export const aggregatorApi = {
  summary: (params: { channelId?: number; dateFrom: string; dateTo: string }) =>
    api.get<AggregatorSummary>(`${BASE}/summary`, { params }),

  entries: (params: { channelId?: number; dateFrom: string; dateTo: string; status?: string }) =>
    api.get<AggregatorEntry[]>(`${BASE}/entries`, { params }),

  settlements: (params: { channelId?: number; dateFrom: string; dateTo: string; status?: string }) =>
    api.get<AggregatorSettlement[]>(`${BASE}/settlements`, { params }),

  recordSettlement: (data: RecordSettlementInput) =>
    api.post<AggregatorSettlement>(`${BASE}/settlements`, data),

  getSettlement: (id: number) =>
    api.get<AggregatorSettlement>(`${BASE}/settlements/${id}`),

  editSettlement: (id: number, data: EditSettlementInput) =>
    api.patch<AggregatorSettlement>(`${BASE}/settlements/${id}`, data),

  deleteSettlement: (id: number) => api.delete(`${BASE}/settlements/${id}`),

  suggestAllocations: (data: { channelId: number; paymentAmount: number; paymentDate: string; periodFrom?: string; periodTo?: string; settlementId?: number }) =>
    api.post<SuggestedAllocations>(`${BASE}/settlements/suggest-allocations`, data),

  duplicateAllocations: () =>
    api.get<DuplicateAllocation[]>(`${BASE}/duplicate-allocations`),

  aging: (params: { channelId?: number }) =>
    api.get<AgingResponse>(`${BASE}/aging`, { params }),

  agingExport: (params: { channelId?: number; ageDays?: number }) =>
    api.get(`${BASE}/aging/export`, { params, responseType: 'blob' }),

  listAliases: (params: { channelId?: number }) =>
    api.get<ChannelAlias[]>(`${BASE}/aliases`, { params }),

  createAlias: (data: { channelId: number; pattern: string }) =>
    api.post<ChannelAlias>(`${BASE}/aliases`, data),

  updateAlias: (id: number, data: { channelId?: number; pattern?: string }) =>
    api.patch<ChannelAlias>(`${BASE}/aliases/${id}`, data),

  deleteAlias: (id: number) => api.delete(`${BASE}/aliases/${id}`),

  listAdjustments: (settlementId: number) =>
    api.get<SettlementAdjustment[]>(`${BASE}/settlements/${settlementId}/adjustments`),

  addAdjustment: (
    settlementId: number,
    data: { adjustmentDate: string; amount: number; kind: AdjustmentKind; note?: string },
  ) => api.post<SettlementAdjustment>(`${BASE}/settlements/${settlementId}/adjustments`, data),

  deleteAdjustment: (settlementId: number, adjustmentId: number) =>
    api.delete(`${BASE}/settlements/${settlementId}/adjustments/${adjustmentId}`),
};
